/**
 * BUSINESS DETAIL — Layout 3 colonne (da wireframe "Azienda").
 *
 * Sidebar sinistra interamente inline-editabile via <EditableField>.
 * Liste emails/phones/addresses gestite da editor dedicati.
 *
 * Campi non editabili in questo momento: nessuno (tutti i campi Business
 * sono editabili inline).
 * Pulsante "Modifica" condizionato al feature flag VITE_ENABLE_EDIT_FORMS.
 */

import { Fragment, useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import BusinessForm from './BusinessForm';
import TaskForm from '../task/TaskForm';
import NoteFormModal from '../note/NoteFormModal';
import CallFormModal from '../call/CallFormModal';
import EmailFormModal from '../email/EmailFormModal';
import MeetingFormModal from '../meeting/MeetingFormModal';
import LinkedinPostFormModal from '../linkedinPost/LinkedinPostFormModal';
import { BusinessAvatar } from '../../components/BusinessAvatar';
import { ACTIVITY_TO_TASK_TYPE, type TaskType } from '../task/taskTypes';
import { useTranslation } from 'react-i18next';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchBusiness, updateBusiness, deleteBusiness } from './businessThunks';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useConfirmDialog } from '../../components/useConfirmDialog';
import { clearCurrentBusiness } from './businessSlice';
import { fetchRelationships } from '../relationship/relationshipThunks';
import { RelationPanels } from '../../components/RelationPanels';
import { ActivityTimeline } from '../../components/ActivityTimeline';
import { DetailTabs } from '../../components/DetailTabs';
import { useEntityActivities } from '../../components/useEntityActivities';
import { useSlotContributors, SLOT_ORDER_LAST } from '../../plugin/useSlotContributors';
import { isValidActivitySection } from '../../components/activitySections';
import { useActivityFocus } from '../../components/useActivityFocus';
import { EditableField, type EditableValue } from '../../components/EditableField';
import { EmailListEditor } from '../../components/EmailListEditor';
import { PhoneListEditor } from '../../components/PhoneListEditor';
import { AddressListEditor } from '../../components/AddressListEditor';
import { useToast } from '../../components/useToast';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { PipelinePlacement } from '../../components/PipelinePlacement';
import { useEnabledPluginEntityTypes } from '../../plugin/useEnabledEntityTypes';
import type { BusinessCreatePayload, BusinessUpdatePayload } from './businessTypes';

// Validazione regex base per nome dominio (es. "example.com", "sub.example.co.uk").
const DOMAIN_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-]*(\.[a-zA-Z0-9][a-zA-Z0-9-]*)*\.[a-zA-Z]{2,}$/;

export default function BusinessDetail() {
  const { id, section, activityId } = useParams<{
    id: string;
    section?: string;
    activityId?: string;
  }>();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const enabledPluginEntityTypes = useEnabledPluginEntityTypes();

  const validateDomain = useCallback(
    (value: EditableValue): string | null => {
      const str = String(value ?? '').trim();
      if (!str) return null;
      return DOMAIN_REGEX.test(str) ? null : t('editableField.domainInvalid');
    },
    [t]
  );
  const toast = useToast();
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const { currentItem: business } = useAppSelector((state) => state.businesses);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  // Drawer di creazione task aperto da ActivityTimeline (bottone "+ Nuova X"
  // sulla tab tipologia). Al save, TaskForm collega il task al Business
  // corrente via linkedEntity (Relationship bidirezionale).
  const [quickDrawer, setQuickDrawer] = useState<{ initialType?: TaskType } | null>(null);
  const [noteFormOpen, setNoteFormOpen] = useState(false);
  const [callFormOpen, setCallFormOpen] = useState(false);
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [meetingFormOpen, setMeetingFormOpen] = useState(false);
  const [linkedinFormOpen, setLinkedinFormOpen] = useState(false);
  const numId = id ? Number(id) : 0;
  const activities = useEntityActivities('Business', numId);

  // Sotto-tab attività pilotati dall'URL (/companies/:id/:section): l'URL è la
  // fonte di verità del filtro, non lo stato interno di useEntityActivities.
  // Tab contribuite dai plugin per questa entita' (TS-107). Le legge la scheda e
  // non la timeline perche' servono anche qui sotto, alla guardia del redirect:
  // uno slug contribuito e' una sezione valida quanto 'notes'.
  const activityTabs = useSlotContributors('Business', 'detail-center:activity-tab');
  // Colonna destra: i plugin possono metterci un riquadro sopra o sotto i
  // pannelli relazione. Stessa coppia di slot del preventivo — vedi
  // EntityDetailSlot in @crm/contracts, dove sono passati quando anche questa
  // scheda ha cominciato a renderli.
  const rightTopContributors = useSlotContributors('Business', 'detail-right:top');
  const rightBottomContributors = useSlotContributors('Business', 'detail-right:bottom');
  // Sotto l'anagrafica: riquadri che completano i dati del record.
  const leftBottomContributors = useSlotContributors('Business', 'detail-left:bottom');
  // Campi dentro "Informazioni", intercalati fra quelli del core per `order` (TS-142).
  const infoContributors = useSlotContributors('Business', 'detail-left:info');
  // Deep-link a un'attività (commento o ?activity= dalla ricerca): id dell'item su
  // cui espandere/scrollare. Il commento specifico lo apre ActivityTools.
  const focusItemId = useActivityFocus(section, activityId);
  const handleSectionChange = useCallback(
    (slug: string | undefined) => {
      navigate(slug ? `/companies/${id}/${slug}` : `/companies/${id}`);
    },
    [id, navigate],
  );

  // Slug sconosciuto (es. /companies/101/foo) -> redirect a "Tutte" (rotta base).
  useEffect(() => {
    if (section != null && !isValidActivitySection(section)
      && !activityTabs.some((c) => c.slug === section))
      navigate(`/companies/${id}`, { replace: true });
  }, [section, id, navigate, activityTabs]);

  useEffect(() => {
    if (numId) dispatch(fetchBusiness(numId));
    return () => {
      dispatch(clearCurrentBusiness());
    };
  }, [numId, dispatch]);

  // Le relazioni non si fetchano qui: le possiede chi le usa (RelationPanels e
  // la timeline, via useRelationshipsSource). Questo resta come refetch
  // esplicito dopo una creazione che aggiunge un link (i drawer qui sotto).
  function refreshRelations() {
    const fqn = getEntityFqn('Business');
    if (numId && fqn) dispatch(fetchRelationships({ className: fqn, classPK: numId }));
  }

  if (!business) return <p>{t('common.loading')}</p>;

  const commitField =
    (field: keyof BusinessCreatePayload) =>
    async (newValue: EditableValue) => {
      try {
        const payload = { businessId: business.businessId, [field]: newValue } as BusinessUpdatePayload;
        await dispatch(updateBusiness(payload)).unwrap();
        toast({ severity: 'success', message: t('common.saved') });
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    };

  // Un testo cancellato arriva come null, e la PATCH legge null come «lascia com'è»: per i dati
  // fiscali, che si correggono svuotandoli, si manda la stringa vuota (TS-142).
  const commitClearable = (field: 'vatNumber' | 'fiscalCode' | 'sdiCode' | 'pecEmail') =>
    (newValue: EditableValue) => commitField(field)(newValue ?? '');

  // Campi core di "Informazioni" con il proprio `order`, fusi e ordinati con i contributi dei
  // plugin come in QuotationDetail: di cento in cento, così un plugin si intercala senza che il
  // core sappia nulla di lui.
  const coreInfoFields: { key: string; order: number; node: ReactNode }[] = [
    {
      key: 'externalCode', order: 100,
      node: (
        <InfoLine label={t('business.externalCode')}>
          <EditableField
            value={business.externalCode}
            variant={{ kind: 'text' }}
            readOnly
            onCommit={commitField('externalCode')}
            label={t('business.externalCode')}
          />
        </InfoLine>
      ),
    },
    {
      key: 'domain', order: 200,
      node: (
        <InfoLine label={t('business.domain')}>
          <EditableField
            value={business.domain}
            variant={{ kind: 'text' }}
            required
            validate={validateDomain}
            onCommit={commitField('domain')}
            label={t('business.domain')}
          />
        </InfoLine>
      ),
    },
    {
      key: 'sector', order: 300,
      node: (
        <InfoLine label={t('business.sector')}>
          <EditableField
            value={business.sector}
            variant={{ kind: 'text' }}
            onCommit={commitField('sector')}
            label={t('business.sector')}
          />
        </InfoLine>
      ),
    },
    {
      key: 'owner', order: 400,
      node: (
        <InfoLine label={t('business.owner')}>
          <EditableField
            value={business.owner}
            variant={{ kind: 'text' }}
            onCommit={commitField('owner')}
            label={t('business.owner')}
          />
        </InfoLine>
      ),
    },
    {
      key: 'type', order: 500,
      node: (
        <InfoLine label={t('business.type')}>
          <EditableField
            value={business.type}
            variant={{ kind: 'text' }}
            onCommit={commitField('type')}
            label={t('business.type')}
          />
        </InfoLine>
      ),
    },
    {
      key: 'employees', order: 600,
      node: (
        <InfoLine label={t('business.employees')}>
          <EditableField
            value={business.employees}
            variant={{ kind: 'text' }}
            onCommit={commitField('employees')}
            label={t('business.employees')}
          />
        </InfoLine>
      ),
    },
    ...(['vatNumber', 'fiscalCode', 'sdiCode', 'pecEmail'] as const).map((field, i) => ({
      key: field,
      order: 700 + i * 100,
      node: (
        <InfoLine label={t(`business.${field}`)}>
          <EditableField
            value={business[field] ?? null}
            variant={{ kind: 'text' }}
            onCommit={commitClearable(field)}
            label={t(`business.${field}`)}
          />
        </InfoLine>
      ),
    })),
  ];

  const infoRows = [
    ...coreInfoFields,
    ...infoContributors.map((c) => ({
      key: `plugin:${c.key}`,
      order: c.order ?? SLOT_ORDER_LAST,
      node: <c.component entityId={business.businessId} />,
    })),
  ].sort((a, b) => a.order - b.order);

  /**
   * La collocazione in bacheca non passa da `commitField`: quello è tipizzato su
   * `BusinessCreatePayload`, e `currentStateId` non è un campo di creazione — sta solo
   * nell'`UpdatePayload`, perché la colonna si sceglie su un'azienda che esiste già.
   */
  const commitCurrentStateId = async (pipelineStateId: number) => {
    try {
      await dispatch(
        updateBusiness({ businessId: business.businessId, currentStateId: pipelineStateId }),
      ).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      throw err;
    }
  };

  // Il backend rifiuta un'azienda con preventivi (TS-103) e chi non ha il permesso: in quei
  // casi si resta sulla scheda, col messaggio del server.
  const handleDelete = async () => {
    try {
      await dispatch(deleteBusiness(business.businessId)).unwrap();
      toast({ severity: 'success', message: t('business.deleted') });
      navigate('/companies');
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  };

  return (
    <div className="detail-page">
      {/* Breadcrumb fuori dalle colonne: torna alle aziende + nome dell'azienda
          corrente (stile mockup: "Aziende / Nome azienda"). */}
      <nav className="detail-crumb" aria-label={t('business.title')}>
        <Link to="/companies" className="detail-crumb__back">&larr; {t('business.title')}</Link>
        <span className="detail-crumb__sep">/</span>
        <span className="detail-crumb__current">{business.name}</span>
      </nav>

      {/* `--stack`: sotto i 1440px le tre colonne diventano due — l'anagrafica
          resta a sinistra della sua misura, la timeline prende tutto il resto e
          i pannelli relazione si incolonnano sotto (vedi _detail.scss). */}
      <div className="detail-layout detail-layout--stack">
        {/* LEFT SIDEBAR — Info azienda */}
        <div className="detail-left">

        {/* Header identità (card): avatar + nome editabile inline reso come
            titolo serif + dominio + email primaria. Stessa struttura di
            PersonDetail: le classi (.card.identity, .id-head, .id-main,
            .id-name, .id-email) sono generiche in _detail.scss, non legate
            all'entità, quindi qui non serve CSS dedicato. */}
        <section className="card identity">
          <div className="id-head">
            <BusinessAvatar name={business.name} size="lg" />
            <div className="id-main">
              <div className="id-name">
                <EditableField
                  value={business.name}
                  variant={{ kind: 'text' }}
                  required
                  onCommit={commitField('name')}
                  label={t('business.companyName')}
                />
              </div>
              {/* Il dominio prende il posto che nel contatto non esiste: riga
                  sotto il nome, sopra l'email. */}
              {business.domain && <div className="id-domain">{business.domain}</div>}
              {business.emailAddresses[0] && (() => {
                const emailAddress = business.emailAddresses.find((e) => e.primary)?.address ?? business.emailAddresses[0].address;
                return (
                  <div className="entity-email-row id-email">
                    <span className="text-sm text-primary">{emailAddress}</span>
                    <IconButton
                      size="small"
                      aria-label={t('common.copy')}
                      onClick={async () => {
                        await navigator.clipboard.writeText(emailAddress);
                        toast({ severity: 'success', message: t('common.copied') });
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </div>
                );
              })()}
            </div>
          </div>
        </section>

        <section className="card panels">
        <Accordion defaultExpanded disableGutters elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className="relation-title">{t('business.info')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {/* Contatti in cima e anagrafica sotto, come nella pagina contatto:
                prima come si raggiunge l'azienda, poi come e' fatta. */}
            <EmailListEditor className="Business" classPK={business.businessId} emailAddresses={business.emailAddresses} />
            <PhoneListEditor className="Business" classPK={business.businessId} phones={business.phones} />
            <AddressListEditor businessId={business.businessId} addresses={business.addresses} />

            {infoRows.map((r) => <Fragment key={r.key}>{r.node}</Fragment>)}

            {/* Collocazione in bacheca (TS-117). In fondo alle informazioni e con etichette
                esplicite ("Pipeline" / "Stato pipeline"): lo stato di pipeline non è lo `status`
                dell'azienda (attiva/inattiva), sono due assi indipendenti. Non compare affatto se
                non esistono pipeline delle aziende. */}
            <PipelinePlacement
              fqn={getEntityFqn('Business')}
              currentStateId={business.currentStateId}
              onCommit={commitCurrentStateId}
            />

          </AccordionDetails>
        </Accordion>
        </section>

        {leftBottomContributors.map((c) => (
          <c.component key={c.key} entityId={business.businessId} />
        ))}

        {/* Pillole `q-btn`, le stesse delle azioni del preventivo: il pulsante
            "Modifica" deve essere identico nelle tre schede di dettaglio. "Modifica"
            dipende dal feature flag, "Elimina" no (FILO-17): la cancellazione chiede
            di digitare la parola, come dalla lista. */}
        <div className="q-actions mt-lg">
          {import.meta.env.VITE_ENABLE_EDIT_FORMS === 'true' && (
            <button type="button" className="q-btn q-btn--primary" onClick={() => setEditDrawerOpen(true)}>
              {t('common.edit')}
            </button>
          )}
          <button
            type="button"
            className="q-btn q-btn--danger"
            onClick={() => confirm.requestConfirm(
              t('business.deleteConfirm', { name: business.name }),
              handleDelete,
              { requireTypedConfirmation: true },
            )}
          >
            {t('common.delete')}
          </button>
        </div>
      </div>

      {/* CENTER — La tab core è "Attività" (timeline + sotto-tab per tipo, gestiti
          da ActivityTimeline). Le sezioni in più non si aggiungono qui: le
          contribuiscono i plugin nello slot `detail-center:tab`, e a comporle è
          DetailTabs (TS-107). */}
      <div className="detail-center">
        <DetailTabs
          entityType="Business"
          entityId={business.businessId}
          coreLabel={t('business.tabs.activities')}
        >
          {activities.loading ? (
            <p className="text-muted">{t('business.loadingActivities')}</p>
          ) : (
            <ActivityTimeline
              items={activities.items}
              filters={activities.filters}
              section={section}
              onSectionChange={handleSectionChange}
              activityTabs={activityTabs}
              entityId={business.businessId}
              onExpandItem={activities.onExpandItem}
              focusItemId={focusItemId}
              emptyMessage={t('business.emptyActivities')}
              searchable
              getCreateAction={(type) => {
                const taskType = ACTIVITY_TO_TASK_TYPE[type];
                if (!taskType) return null;
                // TS-21 Macroarea 2: Note ha entita' dedicata, apre NoteFormModal.
                if (type === 'nota') {
                  return {
                    label: t(`activity.newItem.${type}`),
                    onClick: () => setNoteFormOpen(true),
                  };
                }
                // TS-21 Macroarea 3: Call ha entita' dedicata.
                if (type === 'chiamata') {
                  return {
                    label: t(`activity.newItem.${type}`),
                    onClick: () => setCallFormOpen(true),
                  };
                }
                // Email ora entita' dedicata: apre EmailFormModal.
                if (type === 'email') {
                  return {
                    label: t(`activity.newItem.${type}`),
                    onClick: () => setEmailFormOpen(true),
                  };
                }
                // TS-21 Macroarea 4: Meeting ha entita' dedicata.
                if (type === 'riunione') {
                  return {
                    label: t(`activity.newItem.${type}`),
                    onClick: () => setMeetingFormOpen(true),
                  };
                }
                // TS-32: LinkedinPost ha entita' dedicata, apre LinkedinPostFormModal.
                if (type === 'linkedin') {
                  return {
                    label: t(`activity.newItem.${type}`),
                    onClick: () => setLinkedinFormOpen(true),
                  };
                }
                return {
                  label: t(`activity.newItem.${type}`),
                  onClick: () => setQuickDrawer({ initialType: taskType }),
                };
              }}
            />
          )}
        </DetailTabs>
      </div>

      {/* RIGHT SIDEBAR — Relation panels. Ordine esplicito: contatti prima, i
          target dei plugin dopo. NB: niente pannello "Aziende" — un'azienda non
          si collega ad altre aziende in questa pagina, ed e' il motivo per cui
          qui `targets` non si può omettere.

          'Quotation' va nominato ESPLICITAMENTE (TS-87): finché il preventivo
          era di un plugin arrivava da useEnabledPluginEntityTypes, che per
          costruzione ritorna solo i tipi dei plugin. Diventando core sparirebbe
          da qui in silenzio, e con lui il pannello "Preventivi". */}
      <div className="detail-right">
        {rightTopContributors.map((c) => (
          <c.component key={c.key} entityId={business.businessId} />
        ))}

        <RelationPanels
          source={{ type: 'Business', id: business.businessId }}
          targets={['Person', 'Quotation', ...enabledPluginEntityTypes.map((et) => et.typeName)]}
          onRelationChanged={refreshRelations}
        />

        {rightBottomContributors.map((c) => (
          <c.component key={c.key} entityId={business.businessId} />
        ))}
      </div>

      {/* Modale edit dell'azienda (feature flag): riusa BusinessForm
          in modalita' update; al submit dispatcha updateBusiness e lo
          state aggiornato riflette nel Detail. */}
      {editDrawerOpen && (
        <BusinessForm
          businessId={business.businessId}
          onClose={() => setEditDrawerOpen(false)}
        />
      )}

      {/* Drawer di creazione task aperto dai bottoni "+ Nuova X" nelle
          tab tipologia dell'ActivityTimeline. linkedEntity collega il
          task al Business corrente al salvataggio. */}
      {quickDrawer && (() => {
        const businessFqn = getEntityFqn('Business');
        return (
          <TaskForm
            mode="modal"
            initialType={quickDrawer.initialType}
            linkedEntity={businessFqn
              ? { className: businessFqn, classPK: business.businessId }
              : undefined
            }
            onClose={() => setQuickDrawer(null)}
            onCreated={refreshRelations}
          />
        );
      })()}

      {/* NoteFormModal: stesso pattern di TaskForm modal, ma per l'entita'
          Note esplicita (TS-21 Macroarea 2). */}
      {noteFormOpen && (() => {
        const businessFqn = getEntityFqn('Business');
        return (
          <NoteFormModal
            linkedEntity={businessFqn
              ? { className: businessFqn, classPK: business.businessId }
              : undefined
            }
            onClose={() => setNoteFormOpen(false)}
            onCreated={refreshRelations}
          />
        );
      })()}

      {/* CallFormModal (TS-21 Macroarea 3). */}
      {callFormOpen && (() => {
        const businessFqn = getEntityFqn('Business');
        return (
          <CallFormModal
            linkedEntity={businessFqn
              ? { className: businessFqn, classPK: business.businessId }
              : undefined
            }
            onClose={() => setCallFormOpen(false)}
            onCreated={refreshRelations}
          />
        );
      })()}

      {/* EmailFormModal — entita' Email come Call, no status. */}
      {emailFormOpen && (() => {
        const businessFqn = getEntityFqn('Business');
        return (
          <EmailFormModal
            linkedEntity={businessFqn
              ? { className: businessFqn, classPK: business.businessId }
              : undefined
            }
            onClose={() => setEmailFormOpen(false)}
            onCreated={refreshRelations}
          />
        );
      })()}

      {/* MeetingFormModal (TS-21 Macroarea 4). */}
      {meetingFormOpen && (() => {
        const businessFqn = getEntityFqn('Business');
        return (
          <MeetingFormModal
            linkedEntity={businessFqn
              ? { className: businessFqn, classPK: business.businessId }
              : undefined
            }
            onClose={() => setMeetingFormOpen(false)}
            onCreated={refreshRelations}
          />
        );
      })()}

      {/* LinkedinPostFormModal (TS-32): entità reale LinkedinPost. */}
      {linkedinFormOpen && (() => {
        const businessFqn = getEntityFqn('Business');
        return (
          <LinkedinPostFormModal
            linkedEntity={businessFqn
              ? { className: businessFqn, classPK: business.businessId }
              : undefined
            }
            onClose={() => setLinkedinFormOpen(false)}
            onCreated={refreshRelations}
          />
        );
      })()}

      <ConfirmDialog
        open={confirm.isOpen}
        message={confirm.message}
        {...confirm.options}
        onConfirm={confirm.confirm}
        onCancel={confirm.cancel}
      />
      </div>
    </div>
  );
}

function InfoLine({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="info-row">
      <div className="info-row__label">{label}</div>
      <div className="info-row__value">{children}</div>
    </div>
  );
}
